import axios, { AxiosInstance } from 'axios'
import { extractErrorMessage } from '../utils/errorHandler'

const API_BASE_URL = (process.env.REACT_APP_API_URL as string) || 'http://localhost:8000/api'

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Add token to requests if available
    this.client.interceptors.request.use((config) => {
      // Try different token keys in order of priority
      const hotelAdminToken = localStorage.getItem('token') // Hotel admin token
      const ownerToken = localStorage.getItem('owner_token')
      const adminToken = localStorage.getItem('admin.access_token')
      
      const token = hotelAdminToken || ownerToken || adminToken
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      } else {
        console.warn('No token found in localStorage')
      }
      return config
    })

    // Handle token refresh and error normalization
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // Only redirect if we're not already on login page
          if (!window.location.pathname.includes('/login') && 
              !window.location.pathname.includes('/owner-login') &&
              !window.location.pathname.includes('/admin-login')) {
            // Clear all tokens
            localStorage.removeItem('token')
            localStorage.removeItem('admin.access_token')
            localStorage.removeItem('owner_token')
            localStorage.removeItem('hotel_admin')
            
            // Redirect to appropriate login page based on current path
            if (window.location.pathname.includes('/owner')) {
              window.location.href = '/owner-login'
            } else if (window.location.pathname.includes('/admin')) {
              window.location.href = '/admin-login'
            } else {
              window.location.href = '/login'
            }
          }
        }
        
        // Нормализуем ошибку для более удобной работы
        const normalizedError = new Error(extractErrorMessage(error))
        return Promise.reject(normalizedError)
      }
    )
  }

  // Auth
  async register(email: string, password: string) {
    const response = await this.client.post('/api/auth/register', { email, password })
    return response.data
  }

  async bindCode(code: string) {
    const response = await this.client.post('/api/auth/bind-code', { code })
    return response.data
  }

  async me() {
    const response = await this.client.get('/api/auth/me')
    return response.data
  }

  async logout() {
    const response = await this.client.post('/api/auth/logout')
    return response.data
  }

  async getHotels() {
    const response = await this.client.get('/api/guests/hotels')
    return response.data
  }

  async registerEmail(email: string, password: string, fullName: string, phone?: string) {
    const response = await this.client.post('/api/auth/register-email', {
      email,
      password,
      full_name: fullName,
      phone: phone || null,
    })
    return response.data
  }

  async login(email: string, password: string) {
    const response = await this.client.post('/api/auth/login', {
      email,
      password,
    })
    return response.data
  }

  // Guests  
  async registerGuest(hotelId: string) {
    const response = await this.client.post('/api/guests/', { hotel_id: hotelId })
    // API возвращает: { status, guest_id, session_id, current_state, description, hint, hotel_name }
    // Мы возвращаем как есть, так как преобразование происходит на уровне компонентов
    return response.data
  }

  async submitStep(userId: number, state: string, value: any) {
    const response = await this.client.post(`/api/guests/${userId}/submit-step`, {
      state,
      value,
    })
    return response.data
  }

  async getGuestStatus(userId: number) {
    const response = await this.client.get(`/api/guests/${userId}/status`)
    return response.data
  }

  async getGuestData(userId: number) {
    const response = await this.client.get(`/api/guests/${userId}/data`)
    return response.data
  }

  async resetGuestSession(userId: number) {
    const response = await this.client.delete(`/api/guests/${userId}/reset`)
    return response.data
  }

  async resetGuest(userId: number) {
    const response = await this.client.delete(`/api/guests/${userId}/reset`)
    return response.data
  }

  // Documents
  async uploadDocument(userId: number, file: File) {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.client.post(`/api/documents/${userId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async processDocumentOCR(userId: number, fileId: string, hotelId: string) {
    const response = await this.client.post(`/api/documents/${userId}/process-ocr`, {
      file_id: fileId,
      hotel_id: hotelId,
    })
    return response.data
  }

  async listDocuments(userId: number) {
    const response = await this.client.get(`/api/documents/${userId}/list`)
    return response.data
  }

  async generateDocuments(userId: number, hotelId: string, documentTypes: string[]) {
    const response = await this.client.post(`/api/documents/${userId}/generate`, {
      hotel_id: hotelId,
      document_types: documentTypes,
    })
    return response.data
  }

  async downloadDocument(docId: string) {
    const response = await this.client.get(`/api/documents/${docId}/download`, {
      responseType: 'blob',
    })
    return response.data
  }

  async deleteDocument(docId: string) {
    const response = await this.client.delete(`/api/admin/documents/${docId}`)
    return response.data
  }

  async confirmOCRResults(userId: number, confirmedData?: Record<string, string>) {
    const response = await this.client.post(`/api/guests/${userId}/confirm-ocr`, {
      confirmed_data: confirmedData || {},
    })
    return response.data
  }

  // Payments
  async getTariffs() {
    const response = await this.client.get('/api/payments/tariffs')
    return response.data
  }

  async createInvoice(tariffId: string) {
    const response = await this.client.post('/api/payments/create-invoice', { tariff_id: tariffId })
    return response.data
  }

  async getUserBalance() {
    const response = await this.client.get('/api/payments/balance')
    return response.data
  }

  async getPaymentHistory() {
    const response = await this.client.get('/api/payments/history')
    return response.data
  }

  // Referral
  async becomeManager(hotelId: string) {
    const response = await this.client.post('/api/referral/become-manager', {
      hotel_id: hotelId,
    })
    return response.data
  }

  async useReferralCode(code: string, hotelId: string) {
    const response = await this.client.post('/api/referral/use-code', { 
      promo_code: code,
      hotel_id: hotelId 
    })
    return response.data
  }

  async getReferralStats() {
    const response = await this.client.get('/api/referral/stats')
    return response.data
  }

  async getCommissionDetails() {
    const response = await this.client.get('/api/referral/bonus-history')
    return response.data
  }

  // Guests - методы для работы с гостями
  async getGuestsByUser() {
    const response = await this.client.get('/api/guests/my')
    return response.data
  }

  async getFirstGuest() {
    const guests = await this.getGuestsByUser()
    return guests.data && guests.data.length > 0 ? guests.data[0] : null
  }

  // Admin
  async getAdminPendingDocuments(hotelId: string, page: number = 1, pageSize: number = 20) {
    const response = await this.client.get('/api/admin/documents', {
      params: {
        hotel_id: hotelId,
        page,
        page_size: pageSize,
      },
    })
    return response.data
  }

  async approveDocument(docId: string, hotelId: string, adminId: number, comment?: string) {
    const response = await this.client.post(`/api/admin/documents/${docId}/approve`, {
      hotel_id: hotelId,
      admin_id: adminId,
      comment: comment || null,
    })
    return response.data
  }

  async rejectDocument(docId: string, hotelId: string, adminId: number, reason: string) {
    const response = await this.client.post(`/api/admin/documents/${docId}/reject`, {
      hotel_id: hotelId,
      admin_id: adminId,
      reason,
    })
    return response.data
  }

  async getAdminStats(hotelId: string, adminId?: number) {
    const response = await this.client.get('/api/admin/stats', {
      params: {
        hotel_id: hotelId,
        admin_id: adminId || undefined,
      },
    })
    return response.data
  }

  // New Admin Cabinet API endpoints
  async adminRegister(email: string, password: string, fullName: string, hotelName: string, phone?: string, referralCode?: string) {
    const response = await this.client.post('/api/admin/register', {
      email,
      password,
      full_name: fullName,
      hotel_name: hotelName,
      phone: phone || null,
      referral_code: referralCode || null,
    })
    return response.data
  }

  async adminLogin(email: string, password: string) {
    const response = await this.client.post('/api/admin/login', {
      email,
      password,
    })
    return response.data
  }

  async getAdminProfile() {
    const response = await this.client.get('/api/admin/me')
    return response.data
  }

  async getAdminDocuments(page: number = 1, pageSize: number = 20, status?: string) {
    const response = await this.client.get('/api/admin/documents', {
      params: { page, page_size: pageSize, status },
    })
    return response.data
  }

  async markDocumentViewed(docId: string) {
    const response = await this.client.post(`/api/admin/documents/${docId}/mark-viewed`)
    return response.data
  }

  async getAdminNotifications(unreadOnly: boolean = false, page: number = 1, pageSize: number = 20) {
    const response = await this.client.get('/api/admin/notifications', {
      params: { unread_only: unreadOnly, page, page_size: pageSize },
    })
    return response.data
  }

  async markNotificationRead(notificationId: number) {
    const response = await this.client.post(`/api/admin/notifications/${notificationId}/mark-read`)
    return response.data
  }

  async selectHotelForGuest(guestId: number, adminId: number) {
    const response = await this.client.post(`/api/guests/${guestId}/select-hotel`, {
      admin_id: adminId,
    })
    return response.data
  }

  // Admin Invitations
  async createInvitationCode() {
    const response = await this.client.post('/api/admin/invitations/create')
    return response.data
  }

  async listMyInvitationCodes() {
    const response = await this.client.get('/api/admin/invitations/my')
    return response.data
  }

  async deactivateInvitationCode(code: string) {
    const response = await this.client.delete(`/api/admin/invitations/${code}`)
    return response.data
  }

  async registerWithCode(code: string, email: string, password: string, fullName: string, hotelName: string, phone?: string) {
    const response = await this.client.post('/api/admin/register-protected', {
      code,
      email,
      password,
      full_name: fullName,
      hotel_name: hotelName,
      phone: phone || null,
    })
    return response.data
  }

  async verifyEmail(token: string) {
    const response = await this.client.post('/api/admin/verify-email', {
      token,
    })
    return response.data
  }

  // Payments
  async getPaymentTariffs() {
    const response = await this.client.get('/api/payments/tariffs')
    return response.data
  }

  async createInvoice(tariffId: string, idempotencyKey: string, config?: any) {
    const response = await this.client.post(
      '/api/payments/create-invoice',
      {
        tariff_id: tariffId,
        idempotency_key: idempotencyKey,
      },
      config
    )
    return response.data
  }

  async getAdminSubscription(config?: any) {
    const response = await this.client.get('/api/payments/subscription', config)
    return response.data
  }

  async post(url: string, data?: any, config?: any) {
    const response = await this.client.post(url, data, config)
    return response.data
  }

  async get(url: string, config?: any) {
    const response = await this.client.get(url, config)
    return response.data
  }

  async delete(url: string, config?: any) {
    const response = await this.client.delete(url, config)
    return response.data
  }

  // Owner API
  async ownerLogin(username: string, password: string) {
    const response = await this.client.post('/api/owner/login', {
      username,
      password,
    })
    return response.data
  }

  async getOwnerHotels() {
    const response = await this.client.get('/api/owner/hotels')
    return response.data
  }

  async deleteOwnerHotel(adminId: number) {
    const response = await this.client.delete(`/api/owner/hotels/${adminId}`)
    return response.data
  }

  async getOwnerTemplates() {
    const response = await this.client.get('/api/owner/templates')
    return response.data
  }

  async approveOwnerTemplate(templateId: number) {
    const response = await this.client.post(`/api/owner/templates/${templateId}/approve`)
    return response.data
  }

  async rejectOwnerTemplate(templateId: number) {
    const response = await this.client.delete(`/api/owner/templates/${templateId}`)
    return response.data
  }

  async getOwnerStats() {
    const response = await this.client.get('/api/owner/stats')
    return response.data
  }
}

export const apiClient = new ApiClient()
